/**
 * Completion content for the "approve-selected-services" activity.
 * Per-ServiceRequest approval decisions with read-only selection details.
 * @agentic connections
 */

import {
  Stack,
  Text,
  Textarea,
  Alert,
  Select,
  Skeleton,
  Paper,
  Group,
  Badge,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getDisplayString } from "@medplum/core";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type { Practitioner, ServiceRequest } from "@medplum/fhirtypes";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";
import { useCarePlanServiceRequests } from "@/hooks/domain/useCarePlanServiceRequests";
import {
  updateCarePlanActivity,
  clearServiceApprovalNotificationForTask,
} from "@benchmark/core/actions";
import { toLogSafeError } from "@benchmark/core/utils";
import { showSuccessNotification } from "@benchmark/ui/utils";
import type { CompletionContentProps } from "./DefaultCompletionContent";
import { PriorReviewsSection } from "./PriorReviewsSection";
import {
  getCarePlanIdFromTask,
  getConditionDisplays,
  getDecisionNotes,
  getLatestApprovalNote,
  getLatestSelectionNote,
  getPreferencesText,
  getSelectedServiceDisplay,
  getServiceRequestVisitModeDisplay,
  getServiceTypeDisplay,
  hasSelectedService,
} from "@benchmark/core/resolvers";

const SR_DECISION_OPTIONS = [
  { value: "approved", label: "Approved" },
  { value: "denied", label: "Denied" },
  { value: "needs_more_info", label: "Needs more information" },
] as const;

/** Per-SR decision state. */
type SrDecision = {
  decision: string | null;
  rationale: string;
};

/**
 * Card for a single ServiceRequest with read-only details and a per-SR decision.
 * @agentic connections
 */
const ApprovalServiceCard = ({
  sr,
  index,
  totalCount,
  srDecision,
  onDecisionChange,
  onRationaleChange,
}: {
  sr: ServiceRequest;
  index: number;
  totalCount: number;
  srDecision: SrDecision;
  onDecisionChange: (value: string | null) => void;
  onRationaleChange: (value: string) => void;
}) => {
  const serviceTypeDisplay = getServiceTypeDisplay(sr);
  const conditions = getConditionDisplays(sr);
  const preferences = getPreferencesText(sr);
  const modeDisplay = getServiceRequestVisitModeDisplay(sr);
  const selectionNote = getLatestSelectionNote(sr);
  const decisionHistory = getDecisionNotes(sr);

  const badgeLabel =
    totalCount > 1
      ? `${index + 1} \u00B7 ${serviceTypeDisplay.toUpperCase()}`
      : serviceTypeDisplay.toUpperCase();

  const decisionColor =
    srDecision.decision === "approved"
      ? "green"
      : srDecision.decision === "denied"
        ? "red"
        : srDecision.decision === "needs_more_info"
          ? "orange"
          : "gray";

  return (
    <Paper
      p="md"
      withBorder
      style={
        srDecision.decision
          ? {
              borderColor: `var(--mantine-color-${decisionColor}-4)`,
              borderWidth: 2,
            }
          : undefined
      }
    >
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Badge variant="filled" color="purple" size="lg" radius="sm">
            {badgeLabel}
          </Badge>
          {srDecision.decision && (
            <Badge variant="light" color={decisionColor} size="sm">
              {SR_DECISION_OPTIONS.find((o) => o.value === srDecision.decision)
                ?.label ?? srDecision.decision}
            </Badge>
          )}
        </Group>

        <Group gap={4} wrap="wrap">
          {conditions.map((c) => (
            <Badge key={c} size="xs" variant="light" color="purple">
              {c}
            </Badge>
          ))}
        </Group>

        {preferences && (
          <Text size="sm" c="purple.7" fw={500}>
            Member Preferences: {preferences}
          </Text>
        )}

        <Stack gap={4}>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
              Selected Service:
            </Text>
            <Text size="sm">{getSelectedServiceDisplay(sr)}</Text>
          </Group>

          {modeDisplay !== "—" && (
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
                Service Mode:
              </Text>
              <Text size="sm">{modeDisplay}</Text>
            </Group>
          )}

          {selectionNote?.websiteUrls &&
            selectionNote.websiteUrls.length > 0 && (
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
                  {selectionNote.websiteUrls.length === 1
                    ? "Website:"
                    : "Websites:"}
                </Text>
                <Stack gap={2}>
                  {selectionNote.websiteUrls.map((url, i) => (
                    <Text key={i} size="sm">
                      {url}
                    </Text>
                  ))}
                </Stack>
              </Group>
            )}

          {selectionNote?.rationale && (
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
                Rationale:
              </Text>
              <Text size="sm">{selectionNote.rationale}</Text>
            </Group>
          )}
        </Stack>

        <PriorReviewsSection decisionHistory={decisionHistory} />

        <Select
          label="Decision"
          placeholder="Approve or deny this service..."
          required
          data={SR_DECISION_OPTIONS}
          value={srDecision.decision}
          onChange={onDecisionChange}
          size="sm"
        />

        {srDecision.decision && (
          <Textarea
            label={
              srDecision.decision === "approved"
                ? "Approval Notes (optional)"
                : "Rationale"
            }
            placeholder={
              srDecision.decision === "approved"
                ? "E.g. preferred practitioner, scheduling notes, clinical context..."
                : "Why is this service being denied or flagged?"
            }
            value={srDecision.rationale}
            onChange={(e) => onRationaleChange(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={3}
            size="sm"
          />
        )}
      </Stack>
    </Paper>
  );
};

/**
 * Completion form for approve-selected-services: per-SR decisions + assignment.
 * @agentic connections
 */
export const ApproveSelectedServicesCompletionContent = ({
  task,
  onComplete,
  onDone,
  onCancel,
  onDirtyChange,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextActivityAppliedRule,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  isResolvingNextTaskOwner = false,
}: CompletionContentProps) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const form = useForm<{ decisions: Record<string, SrDecision> }>({
    initialValues: { decisions: {} },
  });
  const carePlanId = getCarePlanIdFromTask(task);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();

  const {
    serviceRequests,
    isLoading: srLoading,
    error: srError,
  } = useCarePlanServiceRequests(carePlanId);

  const selectedServiceRequests = useMemo(
    () => serviceRequests.filter(hasSelectedService),
    [serviceRequests],
  );

  useEffect(() => {
    if (selectedServiceRequests.length === 0) return;
    const prev = form.values.decisions;
    let changed = false;
    const next = { ...prev };
    for (const sr of selectedServiceRequests) {
      if (sr.id && !(sr.id in next)) {
        const priorApproval = getLatestApprovalNote(sr);
        next[sr.id] = priorApproval
          ? { decision: "approved", rationale: "" }
          : { decision: null, rationale: "" };
        changed = true;
      }
    }
    if (changed) {
      form.setFieldValue("decisions", next);
    }
  }, [selectedServiceRequests, form]);

  const decisions = form.values.decisions;

  const allDecided = useMemo(
    () =>
      selectedServiceRequests.length > 0 &&
      selectedServiceRequests.every(
        (sr) => sr.id && decisions[sr.id]?.decision,
      ),
    [selectedServiceRequests, decisions],
  );

  const hasAnyRejection = useMemo(
    () =>
      Object.values(decisions).some(
        (d) => d.decision === "denied" || d.decision === "needs_more_info",
      ),
    [decisions],
  );

  const allApproved = useMemo(
    () =>
      allDecided &&
      Object.values(decisions).every((d) => d.decision === "approved"),
    [allDecided, decisions],
  );

  const steps = useMemo(
    () => [
      {
        id: "details",
        title: "Details",
        subtitle: "Review and decide on each service",
        validate: () => {
          if (!allDecided) {
            return { decisions: "A decision is required for every service" };
          }
          return null;
        },
      },
      ...(!hasAnyRejection
        ? [
            {
              id: "assignment",
              title: "Assignment",
              subtitle: "Review next task assignment",
            },
          ]
        : []),
    ],
    [allDecided, hasAnyRejection],
  );

  const resolveOriginalSelector = useCallback(
    async (
      activityCode: string,
    ): Promise<{ ref: string; display: string } | undefined> => {
      if (!carePlanId) return undefined;
      const cp = await medplum.readResource("CarePlan", carePlanId);
      for (const a of cp.activity ?? []) {
        const ref = a.reference?.reference;
        if (!ref?.startsWith("Task/")) continue;
        const linkedTask = await medplum.readResource(
          "Task",
          ref.replace("Task/", ""),
        );
        if (
          linkedTask.code?.coding?.[0]?.code === activityCode &&
          linkedTask.owner?.reference
        ) {
          const ownerRef = linkedTask.owner.reference.replace(
            "PractitionerRole/",
            "",
          );
          return {
            ref: ownerRef,
            display: linkedTask.owner.display || "Prior Selector",
          };
        }
      }
      return undefined;
    },
    [medplum, carePlanId],
  );

  const findPreviousActivityCode = useCallback(
    async (currentActivityCode: string): Promise<string | undefined> => {
      if (!carePlanId) return undefined;
      const carePlan = await medplum.readResource("CarePlan", carePlanId);
      const activities = carePlan.activity ?? [];

      let currentIndex = -1;
      for (let i = 0; i < activities.length; i++) {
        const a = activities[i];
        const detailCode = a.detail?.code?.coding?.[0]?.code;
        if (detailCode === currentActivityCode) {
          currentIndex = i;
          break;
        }
        const ref = a.reference?.reference;
        if (ref?.startsWith("Task/")) {
          const linkedTask = await medplum.readResource(
            "Task",
            ref.replace("Task/", ""),
          );
          if (linkedTask.code?.coding?.[0]?.code === currentActivityCode) {
            currentIndex = i;
            break;
          }
        }
      }

      if (currentIndex <= 0) return undefined;

      const prev = activities[currentIndex - 1];
      const prevDetailCode = prev.detail?.code?.coding?.[0]?.code;
      if (prevDetailCode) return prevDetailCode;

      const prevRef = prev.reference?.reference;
      if (prevRef?.startsWith("Task/")) {
        const prevTask = await medplum.readResource(
          "Task",
          prevRef.replace("Task/", ""),
        );
        return prevTask.code?.coding?.[0]?.code;
      }
      return undefined;
    },
    [medplum, carePlanId],
  );

  const handleSubmit = useCallback(async () => {
    if (!allDecided) return;

    setSubmitting(true);
    setError(null);

    try {
      const reviewerDisplay = profile
        ? getDisplayString(profile)
        : "Unknown Reviewer";
      const now = new Date().toISOString();

      const summaryParts: string[] = [];
      for (const sr of selectedServiceRequests) {
        if (!sr.id) continue;
        const d = decisions[sr.id];
        if (!d?.decision) continue;
        const serviceLabel = getServiceTypeDisplay(sr);
        const decisionLabel =
          SR_DECISION_OPTIONS.find((o) => o.value === d.decision)?.label ??
          d.decision;
        summaryParts.push(`${serviceLabel}: ${decisionLabel}`);
      }

      // Annotate each SR with its per-SR decision (skip if already approved with no new rationale)
      await Promise.all(
        selectedServiceRequests
          .filter((sr) => sr.id)
          .map((sr) => {
            const d = decisions[sr.id!];
            if (!d?.decision) return Promise.resolve(sr);

            const priorApproval = getLatestApprovalNote(sr);
            const isRedundantApproval =
              d.decision === "approved" && priorApproval && !d.rationale.trim();
            if (isRedundantApproval) return Promise.resolve(sr);

            const notePrefix =
              d.decision === "approved"
                ? "Service Approved"
                : "Service Rejection";
            return medplum.updateResource({
              ...sr,
              note: [
                ...(sr.note ?? []),
                {
                  authorString: reviewerDisplay,
                  time: now,
                  text: `${notePrefix}: ${d.rationale.trim() || SR_DECISION_OPTIONS.find((o) => o.value === d.decision)?.label || d.decision} | Decision: ${d.decision}`,
                },
              ],
            });
          }),
      );

      if (allApproved) {
        const statusReason = `Approved: ${summaryParts.join("; ")}`;
        await onComplete(statusReason);
        await teeForTask({
          task,
          minutes: careMinutes,
          method: "task-completion",
        });
        // The work is done — clear the notice (E2). Best-effort.
        if (task.id) {
          try {
            await clearServiceApprovalNotificationForTask(medplum, task.id);
          } catch (error) {
            console.error(
              "Failed to clear service-approval notification:",
              toLogSafeError(error),
            );
          }
        }
        return;
      }

      // Mixed or all-rejected: restart select-services
      if (!carePlanId) throw new Error("Missing care plan reference");
      const carePlan = await medplum.readResource("CarePlan", carePlanId);
      const currentActivityCode = task.code?.coding?.[0]?.code;
      if (!currentActivityCode)
        throw new Error("Cannot determine current activity code");

      const previousCode = await findPreviousActivityCode(currentActivityCode);
      if (!previousCode)
        throw new Error("Cannot determine previous activity to restart");

      const originalSelector = await resolveOriginalSelector(previousCode);

      const statusReason = `Denied: ${summaryParts.join("; ")}`;

      const afterDraft = await updateCarePlanActivity(medplum, {
        carePlan,
        activityCode: currentActivityCode,
        newStatus: "draft",
        statusReason,
      });

      await updateCarePlanActivity(medplum, {
        carePlan: afterDraft,
        activityCode: previousCode,
        newStatus: "ready",
        statusReason,
        ownerOverride: originalSelector,
        dueDateOverride: new Date().toISOString().split("T")[0],
      });

      // A decision was made (deny / needs-info) — clear the notice (E3).
      // Best-effort; the decision has already been recorded.
      if (task.id) {
        try {
          await clearServiceApprovalNotificationForTask(medplum, task.id);
        } catch (error) {
          console.error(
            "Failed to clear service-approval notification:",
            toLogSafeError(error),
          );
        }
      }

      showSuccessNotification("Decisions recorded — returned for re-selection");
      onDone();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to submit decisions",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    allDecided,
    allApproved,
    decisions,
    onComplete,
    onDone,
    medplum,
    profile,
    task,
    carePlanId,
    selectedServiceRequests,
    findPreviousActivityCode,
    resolveOriginalSelector,
  ]);

  useEffect(() => {
    const anyDecision = Object.values(decisions).some((d) => d.decision);
    const anyRationale = Object.values(decisions).some(
      (d) => d.rationale.trim().length > 0,
    );
    onDirtyChange?.(
      anyDecision || anyRationale || !!nextTaskOwnerOverride || !!careMinutes,
    );
  }, [decisions, nextTaskOwnerOverride, careMinutes, onDirtyChange]);

  if (!carePlanId) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Unable to determine care plan from this task.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={() => handleSubmit()}
        isSubmitting={submitting}
        submitText="Submit Decisions"
        onCancel={onCancel}
      >
        <FormStep id="details">
          <Stack gap="md">
            <Text size="sm">
              Review the services selected by SpecOps for your member. Approve
              or deny each service individually.
            </Text>

            {srLoading && (
              <Stack gap="xs">
                <Skeleton height={16} />
                <Skeleton height={60} />
              </Stack>
            )}

            {srError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="orange"
                variant="light"
              >
                Failed to load service requests: {srError}
              </Alert>
            )}

            {!srLoading &&
              !srError &&
              selectedServiceRequests.length > 0 &&
              selectedServiceRequests.map((sr, index) => (
                <ApprovalServiceCard
                  key={sr.id}
                  sr={sr}
                  index={index}
                  totalCount={selectedServiceRequests.length}
                  srDecision={
                    decisions[sr.id!] ?? { decision: null, rationale: "" }
                  }
                  onDecisionChange={(value) => {
                    form.setFieldValue("decisions", {
                      ...form.values.decisions,
                      [sr.id!]: {
                        ...form.values.decisions[sr.id!],
                        decision: value,
                        rationale:
                          form.values.decisions[sr.id!]?.rationale ?? "",
                      },
                    });
                  }}
                  onRationaleChange={(value) => {
                    form.setFieldValue("decisions", {
                      ...form.values.decisions,
                      [sr.id!]: {
                        ...form.values.decisions[sr.id!],
                        rationale: value,
                      },
                    });
                  }}
                />
              ))}

            {!srLoading &&
              !srError &&
              selectedServiceRequests.length === 0 &&
              serviceRequests.length > 0 && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="orange"
                  variant="light"
                >
                  No services have been selected yet. The Select Services for
                  Referral step may not be complete.
                </Alert>
              )}
          </Stack>
        </FormStep>

        {!hasAnyRejection && (
          <FormStep id="assignment">
            <Stack gap="md">
              <NextTaskOwnerSection
                nextActivityLabel={nextActivityLabel}
                defaultOwner={nextActivityDefaultOwner}
                appliedRule={nextActivityAppliedRule}
                overrideOwner={nextTaskOwnerOverride}
                onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
                isResolving={isResolvingNextTaskOwner}
              />
              <CareTimeActivityDurationField
                minutes={careMinutes}
                onMinutesChange={setCareMinutes}
              />
            </Stack>
          </FormStep>
        )}
      </SteppedForm>
    </Stack>
  );
};
